import { Component, ElementRef, OnInit, Renderer2, OnDestroy, Input } from '@angular/core';

import { LanguageService } from '@core/services/language/language.service';
import { AuthService } from '@services/auth/auth.service';
import { ApiSysService } from '@services/api/api-sys.service';
import { alpha3ToAlpha2 } from '@shared/utils/string-utils';
import { Subscription } from 'rxjs';

interface Language {
  lang_name: string;
}

@Component({
    selector: 'app-language-selector',
    imports: [],
    templateUrl: './language-selector.component.html',
    styleUrls: ['./language-selector.component.scss']
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  public langs: Language[] = [];
  public selectedLanguage: string | null = null;
  public selectedLanguageFlag: string | null = null;
  public dropdownOpen = false;

  @Input() loginValue: boolean | null = false;

  private documentClickListener: () => void = () => { };
  private subscriptions: Subscription[] = [];

  private languageNames: Record<string, string> = {
    'eng': 'English',
    'spa': 'Español',
    'cat': 'Català',
    'fra': 'Français',
    'ita': 'Italiano',
    'deu': 'Deutsch',
    'por': 'Português',
    'rus': 'Русский',
    'ara': 'العربية'
  };

  constructor(
    private langService: LanguageService,
    private authService: AuthService,
    private apiSysService: ApiSysService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
  ) { }

  public ngOnInit(): void {
    this.selectedLanguage = this.langService.getLanguage();

    const langSubscription = this.langService.getSupportedLanguages().subscribe(
      (data: any) => {
        this.langs = data as Language[];
        this.updateSelectedLanguageFlag();
      },
      (error) => {
        console.error('Error fetching supported languages:', error);
      }
    );

    const forceChangeSubscription = this.langService.forceChange$.subscribe(
      (newLang: string | null) => {
        if (newLang) {
          this.selectedLanguage = newLang;
          this.updateSelectedLanguageFlag();
        }
      },
      (error) => {
        console.error('Error in language change subscription:', error);
      }
    );

    this.subscriptions.push(langSubscription, forceChangeSubscription);
  }

  private updateSelectedLanguageFlag(): void {
    if (this.selectedLanguage) {
      this.selectedLanguageFlag = this.convertToAlpha2Code(this.selectedLanguage);
    }
  }

  public convertToAlpha2Code(alpha3: string): string {
    const flagCode = alpha3ToAlpha2(alpha3);
    return flagCode || 'eng';
  }

  public getLanguageCode(langName: string | null): string {
    return langName || 'eng';
  }

  public getLanguageDisplayName(langName: string): string {
    const code = this.getLanguageCode(langName);
    return this.languageNames[code] || langName;
  }

  public trackByLang(index: number, lang: Language): string {
    return lang.lang_name;
  }

  public toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;

    if (this.dropdownOpen) {
      this.removeDocumentClickListener();
      this.setupDocumentClickListener();
    }
  }

  private setupDocumentClickListener(): void {
    this.documentClickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (!this.elementRef.nativeElement.contains(event.target)) {
        this.dropdownOpen = false;
        this.removeDocumentClickListener();
      }
    });
  }

  private applyLanguage(langName: string): void {
    this.selectedLanguage = langName;
    this.selectedLanguageFlag = this.convertToAlpha2Code(langName);
    this.langService.setLanguage(langName);
    this.dropdownOpen = false;
    this.persistLanguageToBackend(langName);
  }

  private persistLanguageToBackend(langName: string): void {
    const langId = this.langService.getLangIdByCode(langName);
    const personnelId = Number(this.authService.getUserId());
    const token = this.authService.getToken();
    if (langId && personnelId) {
      this.apiSysService.patchPersonnel(token, personnelId, { lang_id: langId }).subscribe();
    }
  }

  public selectLanguage(lang: Language): void {
    if (this.selectedLanguage === lang.lang_name) {
      return;
    }
    this.applyLanguage(lang.lang_name);
  }

  private removeDocumentClickListener(): void {
    if (this.documentClickListener) {
      this.documentClickListener();
    }
  }

  public ngOnDestroy(): void {
    this.removeDocumentClickListener();
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}